import { useRef, useState } from 'react';
import UploadImageModal from './UploadImageModal';
import {
    buildMediaKitPhotoUploadUrl,
    deleteMediaKitPhoto,
    fetchMediaKit,
    MediaKitData,
    MediaKitPhoto,
} from '../services/mediaKitService';

import {
    MEDIA_KIT_MAX_PHOTOS,
} from '../utils/mediaKitValidation';

const MAX_PHOTOS = MEDIA_KIT_MAX_PHOTOS;

interface MediaKitPhotoCollageEditProps {
    photos: MediaKitPhoto[];
    athleteUserId?: number;
    onPhotosChange: (photos: MediaKitPhoto[]) => void;
    onUpdated: (data: MediaKitData) => void;
}

function layoutClassForPhotoCount(count: number): string {
    if (count <= 1) {
        return 'media-kit-photo-collage--1';
    }
    if (count === 2) {
        return 'media-kit-photo-collage--2';
    }
    if (count === 3) {
        return 'media-kit-photo-collage--3';
    }
    return 'media-kit-photo-collage--4';
}

function parseUploadedPhoto(json: Record<string, unknown>): MediaKitPhoto | null {
    const rawId = json.id;
    const imageUrl = json.imageUrl;
    const id = typeof rawId === 'number' ? rawId : Number(rawId);
    if (!Number.isFinite(id) || typeof imageUrl !== 'string' || !imageUrl) {
        return null;
    }
    const displayOrder = typeof json.displayOrder === 'number' ? json.displayOrder : 0;
    return { id, imageUrl, displayOrder };
}

function mergeUploadedPhoto(existing: MediaKitData, uploaded: MediaKitPhoto): MediaKitData {
    if (existing.photos.some((photo) => photo.id === uploaded.id)) {
        return existing;
    }
    const photos = [...existing.photos, uploaded].sort((a, b) => a.displayOrder - b.displayOrder);
    return { ...existing, photos };
}

export default function MediaKitPhotoCollageEdit({
    photos,
    athleteUserId,
    onPhotosChange,
    onUpdated,
}: MediaKitPhotoCollageEditProps) {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const pendingUploadRef = useRef<MediaKitPhoto | null>(null);

    const visible = photos.slice(0, MAX_PHOTOS);
    const canAdd = visible.length < MAX_PHOTOS;
    const layoutClass = layoutClassForPhotoCount(visible.length);

    const applyKitUpdate = (kit: MediaKitData) => {
        const pending = pendingUploadRef.current;
        pendingUploadRef.current = null;
        onUpdated(pending ? mergeUploadedPhoto(kit, pending) : kit);
    };

    const refreshPhotos = async () => {
        const kit = await fetchMediaKit(athleteUserId);
        applyKitUpdate(kit);
    };

    const handleDelete = async (photoId: number) => {
        setBusy(true);
        setError(null);
        try {
            await deleteMediaKitPhoto(photoId, athleteUserId);
            await refreshPhotos();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to remove photo');
        } finally {
            setBusy(false);
        }
    };

    const handleUploadResponse = (json: Record<string, unknown>) => {
        const uploaded = parseUploadedPhoto(json);
        if (!uploaded) {
            return;
        }
        pendingUploadRef.current = uploaded;
        if (!photos.some((photo) => photo.id === uploaded.id)) {
            onPhotosChange(
                [...photos, uploaded].sort((a, b) => a.displayOrder - b.displayOrder),
            );
        }
    };

    const handleUploaded = async () => {
        setShowUploadModal(false);
        setBusy(true);
        setError(null);
        try {
            await refreshPhotos();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to refresh photos');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="media-kit-photo-collage-edit">
            {visible.length === 0 ? (
                <div className="media-kit-photo-collage media-kit-photo-collage--empty">
                    <span className="media-kit-photo-collage-empty">No photos yet</span>
                </div>
            ) : (
                <div
                    className={`media-kit-photo-collage media-kit-photo-collage--edit ${layoutClass}`}
                    data-photo-count={visible.length}
                >
                    {visible.map((photo, index) => (
                        <div
                            key={photo.id}
                            className={`media-kit-photo-collage-cell media-kit-photo-collage-item--${index + 1}`}
                        >
                            <img
                                src={photo.imageUrl}
                                alt=""
                                className="media-kit-photo-collage-item"
                            />
                            <button
                                type="button"
                                className="media-kit-photo-remove"
                                onClick={() => handleDelete(photo.id)}
                                disabled={busy}
                                aria-label="Remove photo"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {canAdd && (
                <button
                    type="button"
                    className="media-kit-photo-editor-add-bar"
                    onClick={() => setShowUploadModal(true)}
                    disabled={busy}
                    data-testid="media-kit-add-photo"
                >
                    + Add Photo ({visible.length}/{MAX_PHOTOS})
                </button>
            )}
            {error && <span className="media-kit-error">{error}</span>}
            <UploadImageModal
                open={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                mode="immediate"
                uploadUrl={buildMediaKitPhotoUploadUrl(athleteUserId)}
                title="Upload media kit photo"
                onUploadResponse={handleUploadResponse}
                onUploaded={handleUploaded}
            />
        </div>
    );
}
